/* Habeas Data public form — Iter 44.
 * Any person (registered or not) can submit a formal Habeas Data request.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, CheckCircle2, Shield } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { api, friendlyError } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const REQUEST_TYPES = [
  { value: 'access',   label: 'Acceso a mis datos' },
  { value: 'update',   label: 'Actualización o rectificación' },
  { value: 'delete',   label: 'Eliminación / supresión' },
  { value: 'revoke',   label: 'Revocación de autorización' },
  { value: 'complaint', label: 'Queja o denuncia' },
];

export default function HabeasData() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    full_name: '', email: '', document_id: '',
    request_type: 'access', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [consent, setConsent] = useState(false);

  const onChange = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast({ title: 'Debes aceptar la política', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      const res = await api.privacySubmitHabeasData(form);
      setTicket(res);
    } catch (err) {
      toast({ title: 'No pudimos enviar', description: friendlyError(err), variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="habeas-data-page">
      <SEO title="Habeas Data · habita.co" description="Ejerce tus derechos como titular de datos personales bajo la Ley 1581 de 2012." />
      <Header />
      <div className="bg-habita-cream border-b border-habita/10">
        <div className="max-w-2xl mx-auto px-4 lg:px-8 py-10 lg:py-14">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-habita mb-4">
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <span className="w-11 h-11 rounded-2xl bg-habita text-white flex items-center justify-center"><Shield className="w-5 h-5" /></span>
            <p className="text-xs uppercase tracking-[0.25em] text-habita font-semibold">Ley 1581 de 2012</p>
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl font-bold text-habita-dark leading-tight">Formulario Habeas Data</h1>
          <p className="text-gray-600 mt-3 max-w-xl leading-relaxed">Ejerce tus derechos de acceso, actualización, supresión o revocación de tus datos personales. Responderemos dentro de los 15 días hábiles.</p>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 lg:px-8 py-10 lg:py-14">
        {ticket ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center" data-testid="habeas-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <h2 className="font-serif text-2xl font-bold text-emerald-900">Solicitud enviada</h2>
            <p className="text-sm text-emerald-800 mt-2">{ticket.message}</p>
            <p className="text-xs text-emerald-700 mt-3">Ticket: <strong>{ticket.ticket_id}</strong></p>
            <Link to="/" className="mt-6 inline-flex h-10 px-5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 font-semibold items-center gap-2">
              Volver al inicio
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="habeas-form">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nombre completo *</label>
              <input type="text" required minLength={2} maxLength={120} value={form.full_name} onChange={onChange('full_name')}
                     className="w-full h-11 px-3 rounded-lg border border-gray-200 focus:border-habita outline-none text-sm"
                     data-testid="habeas-full-name" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Correo electrónico *</label>
              <input type="email" required value={form.email} onChange={onChange('email')}
                     className="w-full h-11 px-3 rounded-lg border border-gray-200 focus:border-habita outline-none text-sm"
                     data-testid="habeas-email" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Documento de identidad *</label>
              <input type="text" required minLength={4} maxLength={40} value={form.document_id} onChange={onChange('document_id')}
                     placeholder="CC / CE / Pasaporte"
                     className="w-full h-11 px-3 rounded-lg border border-gray-200 focus:border-habita outline-none text-sm"
                     data-testid="habeas-document" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de solicitud *</label>
              <select required value={form.request_type} onChange={onChange('request_type')}
                      className="w-full h-11 px-3 rounded-lg border border-gray-200 focus:border-habita outline-none text-sm bg-white"
                      data-testid="habeas-type">
                {REQUEST_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Descripción de tu solicitud *</label>
              <textarea required minLength={10} maxLength={5000} rows={6} value={form.message} onChange={onChange('message')}
                        placeholder="Cuéntanos qué información deseas acceder, actualizar, suprimir o revocar."
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-habita outline-none text-sm"
                        data-testid="habeas-message" />
            </div>
            <label className="flex items-start gap-2 text-xs text-gray-700" data-testid="habeas-consent">
              <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5" required />
              <span>
                Autorizo a Habita a tratar los datos de este formulario para gestionar mi solicitud, conforme a la{' '}
                <Link to="/politica-datos" className="text-habita hover:underline">Política de Tratamiento de Datos</Link>.
              </span>
            </label>
            <button type="submit" disabled={submitting || !consent}
                    className="w-full h-11 rounded-full bg-habita text-white font-semibold hover:bg-habita-dark disabled:opacity-40 flex items-center justify-center gap-2"
                    data-testid="habeas-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Enviar solicitud
            </button>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
}
